import { t } from '@lingui/core/macro';
import { useState } from 'react';

import { useUserOptions } from '@/api/user';
import {
  encodeFilterFieldName,
  FilterField,
  InfiniteComboboxPickerFilter,
  InfiniteScrollCategoryFilter,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import type { FilterComponentProps } from '@/providers/TableState';

const PAGE_SIZE = 50;

type Props = FilterComponentProps & {
  mode?: 'multiple';
};

const UserFilter = ({ name, label, infoPopover, compact = false, mode = 'multiple' }: Props) => {
  const form = useFilterForm();
  const [search, setSearch] = useState<string>('');

  const { userOptions, error, hasMore, isLoading, isFetchingNextPage, fetchNextPage } =
    useUserOptions({ search, pageSize: PAGE_SIZE });

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="user-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <InfiniteComboboxPickerFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              placeholder={t`Select Users...`}
              loadingPlaceholder={t`Loading Users...`}
              options={userOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              notFoundContent={t`No users were found...`}
              onSearch={setSearch}
            />
          ) : (
            <InfiniteScrollCategoryFilter
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={userOptions}
              isLoading={isLoading}
              isLoadingMore={isFetchingNextPage}
              hasMore={hasMore}
              error={error}
              loadMore={fetchNextPage}
              searchText={search}
              onSearch={setSearch}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default UserFilter;
